import { useEffect, useState, useRef, useCallback } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type {
  ExhibitionsListPageData,
  ExhibitionVenue,
  ExhibitionListItem,
} from '@/types/exhibition'

/*
  Figma: node 260:1535 (Exhibitions list)
  - Page heading: "Exhibitions", h1-title (180px, alfarn-2, weight 700)
  - Map: full-width, Snazzy Maps black-and-white style, pins for active venues
  - Clicking a pin: shows venue detail section below map (image + details side by side)
  - Divider (thick)
  - "Current Exhibitions" then "Past Exhibitions" section headings
    (h1-large via .mig-exhibitions-section-heading); a section is hidden
    when it has no exhibitions (June 2026 UAT feedback — replaced the old
    "See All Exhibitions Past & Present" combined heading)
  - Per section: 2-column grid of exhibition/place cards (1:1 image, venue
    name h3-xlarge 48px, exhibition date, short description with "More >" link)
  - No back button (removed per Figma annotation)
*/

/* ---- Snazzy Maps "Black and White" style (ID 31113) ---- */
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#444444' }] },
  { featureType: 'administrative.locality', elementType: 'geometry.stroke', stylers: [{ visibility: 'simplified' }, { hue: '#ff0042' }] },
  { featureType: 'administrative.neighborhood', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'administrative.land_parcel', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'landscape', elementType: 'all', stylers: [{ color: '#f2f2f2' }] },
  { featureType: 'landscape', elementType: 'geometry.fill', stylers: [{ visibility: 'on' }, { color: '#ffffff' }] },
  { featureType: 'landscape.man_made', elementType: 'labels.text.fill', stylers: [{ visibility: 'on' }, { color: '#000000' }] },
  { featureType: 'landscape.natural.landcover', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'landscape.natural.terrain', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road', elementType: 'all', stylers: [{ saturation: -100 }, { lightness: 45 }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ visibility: 'simplified' }, { color: '#000000' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road.arterial', elementType: 'labels.icon', stylers: [{ visibility: 'off' }, { color: '#000000' }] },
  { featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.line', elementType: 'geometry.stroke', stylers: [{ visibility: 'simplified' }, { color: '#000000' }] },
  { featureType: 'water', elementType: 'all', stylers: [{ color: '#ffffff' }, { visibility: 'on' }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ visibility: 'simplified' }] },
]

/* ---- Custom map pin with Migration Galleries logo ---- */
const LOGO_B64 = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTU3IiBoZWlnaHQ9IjEwNyIgdmlld0JveD0iMCAwIDE1NyAxMDciIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0zMi40NiA4Ni41NTc4TDQ5LjE3MiAxMDYuMjE4SDY0LjkyTDMyLjQ2IDY4LjAzMDhMMCAxMDYuMjE4SDE1Ljc0OEwzMi40NiA4Ni41NTc4WiIgZmlsbD0iYmxhY2siLz4KPHBhdGggZD0iTTEyNC4xIDg2LjU1NzhMMTQwLjgxMiAxMDYuMjE4SDE1Ni41NkwxMjQuMSA2OC4wMzA4TDkxLjY0MDEgMTA2LjIxOEgxMDcuMzg4TDEyNC4xIDg2LjU1NzhaIiBmaWxsPSJibGFjayIvPgo8cGF0aCBkPSJNNzguMjgwMyAxOS42Nkw2MS41NjgzIDEuMTQ0NDFlLTA1SDQ1LjgyMDNMNzguMjgwMyAzOC4xODdMMTEwLjc0IDEuMTQ0NDFlLTA1SDk0Ljk5MjNMNzguMjgwMyAxOS42NloiIGZpbGw9ImJsYWNrIi8+Cjwvc3ZnPgo='
const PIN_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="48" height="67" viewBox="0 0 48 67">
  <path d="M24 0C10.745 0 0 10.745 0 24c0 16.8 24 43.2 24 43.2S48 40.8 48 24C48 10.745 37.255 0 24 0z" fill="#000000"/>
  <circle cx="24" cy="22" r="15" fill="#FFFFFF"/>
  <image href="${LOGO_B64}" x="14" y="14" width="20" height="14"/>
</svg>`

/* ---- Helper: wait for Google Maps JS API (loaded by Liquid partial) ----
   The googlemaps Liquid partial exposes window.googleMapsReady, a promise that
   resolves when Google's async script invokes googleMapCallback. We await that
   promise instead of polling. A 15s timeout guards against network failures. */
function waitForGoogleMaps(timeoutMs = 15000): Promise<void> {
  const ready = (window as any).googleMapsReady as Promise<void> | undefined
  if (!ready) {
    // Fallback: if the partial wasn't loaded, check for the API directly
    if ((window as any).google?.maps) return Promise.resolve()
    return Promise.reject(new Error('Google Maps partial not loaded'))
  }
  return Promise.race([
    ready,
    new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('Google Maps load timeout')), timeoutMs)
    ),
  ])
}

/* ---- Helper: format date string (YYYY-MM-DD or ISO) to DD/MM/YYYY ---- */
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

/* ---- Helper: format date as DD.MM.YY ---- */
function formatDateShort(dateStr?: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return `${day}.${month}.${year}`
}

/* ---- Helper: format a start-end date range as DD.MM.YY - DD.MM.YY ---- */
function formatDateRange(start?: string | null, end?: string | null): string {
  const s = formatDateShort(start)
  const e = formatDateShort(end)
  if (s && e) return `${s} - ${e}`
  return s || e || ''
}

/* ---- Long-form date range: "25 August - 27 October, 2026" ----
   Matches the Exhibition Detail page heading date format. */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
function parseDate(d?: string | null): Date | null {
  if (!d) return null
  const dt = new Date(d)
  return isNaN(dt.getTime()) ? null : dt
}
function formatLongDateRange(start?: string | null, end?: string | null): string {
  const s = parseDate(start)
  const e = parseDate(end)
  if (!s && !e) return ''
  if (s && !e) return `${s.getDate()} ${MONTHS[s.getMonth()]}, ${s.getFullYear()}`
  if (!s && e) return `${e!.getDate()} ${MONTHS[e!.getMonth()]}, ${e!.getFullYear()}`
  const sStr = `${s!.getDate()} ${MONTHS[s!.getMonth()]}`
  const eStr = `${e!.getDate()} ${MONTHS[e!.getMonth()]}`
  if (s!.getFullYear() === e!.getFullYear()) {
    return `${sStr} - ${eStr}, ${e!.getFullYear()}`
  }
  return `${sStr}, ${s!.getFullYear()} - ${eStr}, ${e!.getFullYear()}`
}

/* ---- Helper: strip HTML tags (for plain-text preview of rich descriptions) ---- */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

/* ---- Divider — 12px thick used between map and grid sections. ---- */
function Divider({ height = 1 }: { height?: number }) {
  if (height === 12) return <div className="mig-divider-thick-12" />
  return <div className="mig-divider-line" />
}


/* ======================================================================
   VenueExhibitionsList - exhibitions at the selected venue, shown
   directly when a map pin is clicked. Always rendered as a 2-column
   grid using the same .mig-exhibitions-row pattern as the Past &
   Present grid below, so the visual rhythm matches regardless of
   count. A single exhibition appears as one card in a row with a
   spacer to its right.
   ====================================================================== */
function VenueExhibitionsList({
  venue,
  exhibitions,
}: {
  venue: ExhibitionVenue
  exhibitions: ExhibitionListItem[]
}) {
  const venueExhibitions = exhibitions
    .filter((ex) => String(ex.venue ?? '') === String(venue.id))
    .sort((a, b) => {
      // Past first, then active/upcoming. Within each group ascending by
      // end_date so the list reads chronologically (oldest → newest).
      // (The main grid below now splits Current/Past into sections with
      // its own ordering; this venue list keeps its original order.)
      const now = Date.now()
      const da = a.end_date ? new Date(a.end_date).getTime() : 0
      const db = b.end_date ? new Date(b.end_date).getTime() : 0
      const aPast = da < now
      const bPast = db < now
      if (aPast && !bPast) return -1
      if (!aPast && bPast) return 1
      return da - db
    })

  if (venueExhibitions.length === 0) return null

  // Chunk into rows of 2 — mirrors the Past & Present grid below.
  const rows: ExhibitionListItem[][] = []
  for (let i = 0; i < venueExhibitions.length; i += 2) {
    rows.push(venueExhibitions.slice(i, i + 2))
  }

  return (
    <div className="mig-venue-exhibitions-list">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="mig-exhibitions-row">
          {row.map((exhibition) => (
            <VenueExhibitionCard
              key={exhibition.id}
              exhibition={exhibition}
              venue={venue}
            />
          ))}
          {row.length === 1 && (
            <div className="mig-exhibitions-row-spacer" aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  )
}

/* ======================================================================
   VenueExhibitionCard - horizontal card shown under a selected venue.
   Layout: venue main image (left) + exhibition details (right).
   Details: exhibition name, artists (comma-separated), long-form date
   range, venue name + address + Get Directions + optional phone, venue
   description with "more..." link to the exhibition detail page.
   ====================================================================== */
function VenueExhibitionCard({
  exhibition,
  venue,
}: {
  exhibition: ExhibitionListItem
  venue: ExhibitionVenue
}) {
  const imageUrl =
    venue.main_image?.versions?.large ||
    venue.main_image?.versions?.card ||
    venue.main_image?.url ||
    exhibition.images?.[0]?.file?.url ||
    null
  const title = exhibition.exhibition_name || venue.venue_name || 'Exhibition'
  const artistsLine = (exhibition.artist_names ?? []).join(', ')
  const dateRange = formatLongDateRange(exhibition.start_date, exhibition.end_date)
  // Exhibition's own description (rich HTML, stripped before display) —
  // per June 2026 UAT feedback this no longer falls back to the venue
  // short description; without one the block (incl. Read More) is hidden.
  const description = exhibition.description || ''

  const lat = venue.address_latitude
  const lng = venue.address_longitude
  const hasCoords =
    lat !== null && lat !== undefined && lat !== '' &&
    lng !== null && lng !== undefined && lng !== ''
  const directionsUrl = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${lat},${lng}`
      )}`
    : null

  return (
    <div className="mig-venue-exhibition-card">
      {/* Venue main image - links to exhibition detail */}
      <a
        href={`/exhibitions/${exhibition.url_slug}`}
        className="mig-venue-exhibition-card-image"
      >
        {imageUrl ? (
          <img src={imageUrl} alt={venue.venue_name || title} loading="lazy" decoding="async" />
        ) : (
          <span className="mig-venue-exhibition-card-placeholder">Venue shot from outside</span>
        )}
      </a>

      {/* Details below image */}
      <div className="mig-venue-exhibition-card-details">
        <div className="mig-card-title-stack">
          <h2 className="mig-venue-exhibition-card-title">{title}</h2>
          {artistsLine && (
            <p className="mig-venue-exhibition-card-artists">{artistsLine}</p>
          )}
          {dateRange && (
            <p className="mig-venue-exhibition-card-date">{dateRange}</p>
          )}
        </div>

        {(venue.venue_name || venue.venue_address) && (
          <div className="mig-venue-exhibition-card-venue">
            {venue.venue_name && (
              <p className="mig-venue-exhibition-card-venue-name">{venue.venue_name}</p>
            )}
            {venue.venue_address && (
              <p className="mig-venue-exhibition-card-venue-address">{venue.venue_address}</p>
            )}
            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mig-venue-exhibition-card-directions"
              >
                Get Directions &gt;
              </a>
            )}
          </div>
        )}

        {description && (
          <div className="mig-venue-exhibition-card-description">
            <p className="mig-card-description-clamp">{stripHtml(description)}</p>
            <a href={`/exhibitions/${exhibition.url_slug}`}>Read More &gt;</a>
          </div>
        )}
      </div>
    </div>
  )
}

/* ======================================================================
   ExhibitionGridCard - vertical card used in the "See All Exhibitions
   Past & Present" grid. Same content as VenueExhibitionCard (exhibit
   name, artists, long date range, venue block with Get Directions,
   description with "more..."), but stacked with the picture on top.
   ====================================================================== */
function ExhibitionGridCard({
  exhibition,
  venue,
}: {
  exhibition: ExhibitionListItem
  venue: ExhibitionVenue
}) {
  const imageUrl =
    venue.main_image?.versions?.large ||
    venue.main_image?.versions?.card ||
    venue.main_image?.url ||
    exhibition.images?.[0]?.file?.url ||
    null
  const title = exhibition.exhibition_name || venue.venue_name || 'Exhibition'
  const artistsLine = (exhibition.artist_names ?? []).join(', ')
  const dateRange = formatLongDateRange(exhibition.start_date, exhibition.end_date)
  // Exhibition's own description — same rule as VenueExhibitionCard above.
  const description = exhibition.description || ''

  const lat = venue.address_latitude
  const lng = venue.address_longitude
  const hasCoords =
    lat !== null && lat !== undefined && lat !== '' &&
    lng !== null && lng !== undefined && lng !== ''
  const directionsUrl = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${lat},${lng}`
      )}`
    : null

  return (
    <div className="mig-exhibition-grid-card">
      {/* Image - 1:1 aspect ratio, links to detail */}
      <a
        href={`/exhibitions/${exhibition.url_slug}`}
        className="mig-exhibition-grid-card-image"
      >
        {imageUrl ? (
          <img src={imageUrl} alt={title} loading="lazy" decoding="async" />
        ) : (
          <span className="mig-exhibition-grid-card-placeholder">Venue shot from outside</span>
        )}
      </a>

      {/* Details below image */}
      <div className="mig-exhibition-grid-card-details">
        <div className="mig-card-title-stack">
          <h2 className="mig-exhibition-grid-card-title">{title}</h2>
          {artistsLine && (
            <p className="mig-exhibition-grid-card-artists">{artistsLine}</p>
          )}
          {dateRange && (
            <p className="mig-exhibition-grid-card-date">{dateRange}</p>
          )}
        </div>

        {(venue.venue_name || venue.venue_address) && (
          <div className="mig-exhibition-grid-card-venue">
            {venue.venue_name && (
              <p className="mig-exhibition-grid-card-venue-name">{venue.venue_name}</p>
            )}
            {venue.venue_address && (
              <p className="mig-exhibition-grid-card-venue-address">{venue.venue_address}</p>
            )}
            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mig-exhibition-grid-card-directions"
              >
                Get Directions &gt;
              </a>
            )}
          </div>
        )}

        {description && (
          <div className="mig-exhibition-grid-card-description">
            <p className="mig-card-description-clamp">{stripHtml(description)}</p>
            <a href={`/exhibitions/${exhibition.url_slug}`}>Read More &gt;</a>
          </div>
        )}
      </div>
    </div>
  )
}

/* ======================================================================
   ExhibitionRows - renders pre-chunked rows of 2 ExhibitionGridCards,
   padding an odd final row with a spacer. Shared by the Current and
   Past Exhibitions sections so the grid markup lives in one place.
   ====================================================================== */
function ExhibitionRows({
  rows,
  venuesById,
}: {
  rows: ExhibitionListItem[][]
  venuesById: Map<string, ExhibitionVenue>
}) {
  return (
    <>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="mig-exhibitions-row">
          {row.map((exhibition) => {
            const linkedVenue = venuesById.get(String(exhibition.venue ?? ''))
            if (!linkedVenue) return null
            return (
              <ExhibitionGridCard
                key={exhibition.id}
                exhibition={exhibition}
                venue={linkedVenue}
              />
            )
          })}
          {/* Pad last row if odd */}
          {row.length === 1 && (
            <div className="mig-exhibitions-row-spacer" aria-hidden="true" />
          )}
        </div>
      ))}
    </>
  )
}

/* ======================================================================
   Main Exhibitions List Page
   ====================================================================== */
export default function Exhibitions() {
  const data = usePageData<ExhibitionsListPageData>()
  const venues = data?.venues ?? []
  const exhibitions = data?.exhibitions ?? []

  const [visible, setVisible] = useState(false)
  const [selectedVenue, setSelectedVenue] = useState<ExhibitionVenue | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapError, setMapError] = useState(false)

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const venueDetailRef = useRef<HTMLDivElement>(null)

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Initialize Google Map
  const initMap = useCallback(() => {
    if (!mapRef.current || !(window as any).google?.maps) return

    // Calculate bounds from venue coordinates
    const venuesWithCoords = venues.filter((v) => {
      const lat = parseFloat(String(v.address_latitude ?? ''))
      const lng = parseFloat(String(v.address_longitude ?? ''))
      return !isNaN(lat) && !isNaN(lng)
    })

    // Default center: Sydney, Australia
    let center = { lat: -33.8688, lng: 151.2093 }
    let zoom = 11

    if (venuesWithCoords.length === 1) {
      center = {
        lat: parseFloat(String(venuesWithCoords[0].address_latitude)),
        lng: parseFloat(String(venuesWithCoords[0].address_longitude)),
      }
      zoom = 13
    }

    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom,
      styles: MAP_STYLES,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    })

    mapInstanceRef.current = map

    // Single pass over venuesWithCoords: parse the lat/lng once,
    // extend the bounds rect, and instantiate the marker — folds
    // the previous forEach (bounds) + .map (markers) into one loop.
    const pinIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(PIN_SVG),
      scaledSize: new google.maps.Size(48, 67),
      anchor: new google.maps.Point(24, 67),
    }
    const bounds = new google.maps.LatLngBounds()

    markersRef.current = venuesWithCoords.map((venue) => {
      const position = {
        lat: parseFloat(String(venue.address_latitude)),
        lng: parseFloat(String(venue.address_longitude)),
      }
      bounds.extend(position)

      const marker = new google.maps.Marker({
        position,
        map,
        title: venue.venue_name,
        icon: pinIcon,
        cursor: 'pointer',
      })

      marker.addListener('click', () => {
        setSelectedVenue(venue)
        // Scroll to venue detail section after a brief render delay
        setTimeout(() => {
          venueDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      })

      return marker
    })

    // Fit only when there is more than one pin — fitBounds on a
    // single-point rect would zoom in to the maximum level.
    if (venuesWithCoords.length > 1) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
    }

    setMapLoaded(true)
  }, [venues])

  // Wait for Google Maps API (loaded server-side by Liquid partial) and init
  useEffect(() => {
    waitForGoogleMaps()
      .then(() => initMap())
      .catch(() => setMapError(true))
  }, [initMap])

  // Categorise into Current (still running or upcoming: end_date >= today)
  // and Past (end_date < today; missing/unparseable end_date counts as
  // past, matching the page's previous treatment of it as timestamp 0).
  // Current is sorted ascending by end_date (soonest-ending first);
  // Past is sorted descending (most recently ended first).
  const now = Date.now()
  const endTime = (ex: ExhibitionListItem) =>
    ex.end_date ? new Date(ex.end_date).getTime() || 0 : 0
  const currentExhibitions = exhibitions
    .filter((ex) => endTime(ex) >= now)
    .sort((a, b) => endTime(a) - endTime(b))
  const pastExhibitions = exhibitions
    .filter((ex) => endTime(ex) < now)
    .sort((a, b) => endTime(b) - endTime(a))

  // Build a venue lookup so each exhibition can resolve its linked venue record
  const venuesById = new Map<string, ExhibitionVenue>()
  venues.forEach((v) => venuesById.set(String(v.id), v))

  // Split a section's exhibitions into rows of 2 for the grid
  const chunkRows = (items: ExhibitionListItem[]): ExhibitionListItem[][] => {
    const rows: ExhibitionListItem[][] = []
    for (let i = 0; i < items.length; i += 2) {
      rows.push(items.slice(i, i + 2))
    }
    return rows
  }
  const currentRows = chunkRows(currentExhibitions)
  const pastRows = chunkRows(pastExhibitions)

  return (
    <section className="mig-page-section">
      {/* Page header - "Exhibitions" h1-title page (180px) */}
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-exhibitions-page-title">Exhibitions</h1>
      </div>

      {/* Map + Venue detail + Exhibition grid */}
      <div className="mig-exhibitions-content">
        {/* ---- Google Map section ---- */}
        <div className="mig-exhibitions-map-wrapper">
          {mapError ? (
            <div className="mig-exhibitions-map-error">
              <span className="mig-exhibitions-map-error-text">Map loading...</span>
            </div>
          ) : (
            <div ref={mapRef} className="mig-exhibitions-map" />
          )}
        </div>

        {/* ---- Exhibitions at the selected venue (shown when a pin is clicked) ---- */}
        {selectedVenue && (
          <div ref={venueDetailRef} className="mig-exhibitions-venue-detail">
            <VenueExhibitionsList
              venue={selectedVenue}
              exhibitions={exhibitions}
            />
          </div>
        )}

        {/* ---- Thick divider ---- */}
        <Divider height={12} />

        {/* ---- Current / Past exhibition sections (2-column card grids).
             A section only renders when it has exhibitions; when both are
             empty the shared "No exhibitions yet" message shows instead. ---- */}
        {currentRows.length > 0 && (
          <>
            <div className="mig-exhibitions-section-heading">
              <p>Current Exhibitions</p>
            </div>
            <div className="mig-exhibitions-grid">
              <ExhibitionRows rows={currentRows} venuesById={venuesById} />
            </div>
          </>
        )}

        {pastRows.length > 0 && (
          <>
            <div className="mig-exhibitions-section-heading">
              <p>Past Exhibitions</p>
            </div>
            <div className="mig-exhibitions-grid">
              <ExhibitionRows rows={pastRows} venuesById={venuesById} />
            </div>
          </>
        )}

        {currentRows.length === 0 && pastRows.length === 0 && (
          <p className="mig-exhibitions-empty">No exhibitions yet. Check back soon.</p>
        )}
      </div>
    </section>
  )
}
